import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, primeCsrf } from './api';
import type { Paginated } from './paginated';
import type { DocumentData } from '@/types/api';

const CLIENT_KEY = (clientId: number, page: number) => ['documents', 'client', clientId, page];
const EXPIRING_KEY = (within: number) => ['documents', 'expiring', within];

export function useDocuments(clientId: number | undefined, page = 1) {
  return useQuery({
    queryKey: clientId == null ? ['documents', 'client', null] : CLIENT_KEY(clientId, page),
    enabled: clientId != null,
    queryFn: async (): Promise<Paginated<DocumentData>> => {
      const { data } = await api.get<Paginated<DocumentData>>(
        `/v1/clients/${clientId}/documents`,
        { params: { page } },
      );
      return data;
    },
  });
}

export type DocumentUploadInput = {
  category: string;
  title: string;
  description?: string;
  expires_at?: string;
  reminder_days_before?: number;
  file: File;
};

export function useUploadDocument(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: DocumentUploadInput): Promise<DocumentData> => {
      await primeCsrf();
      const fd = new FormData();
      fd.append('category', input.category);
      fd.append('title', input.title);
      if (input.description) fd.append('description', input.description);
      if (input.expires_at) fd.append('expires_at', input.expires_at);
      if (input.reminder_days_before != null)
        fd.append('reminder_days_before', String(input.reminder_days_before));
      fd.append('file', input.file);

      const { data } = await api.post<DocumentData>(
        `/v1/clients/${clientId}/documents`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['documents', 'client', clientId] });
      qc.invalidateQueries({ queryKey: ['timeline', clientId] });
      qc.invalidateQueries({ queryKey: ['documents', 'expiring'] });
    },
  });
}

export function useUpdateDocument(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      patch,
    }: {
      id: number;
      patch: {
        title?: string;
        description?: string | null;
        category?: string;
        expires_at?: string | null;
        reminder_days_before?: number | null;
      };
    }): Promise<DocumentData> => {
      await primeCsrf();
      const { data } = await api.patch<DocumentData>(
        `/v1/clients/${clientId}/documents/${id}`,
        patch,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['documents', 'client', clientId] });
      qc.invalidateQueries({ queryKey: ['documents', 'expiring'] });
      qc.invalidateQueries({ queryKey: ['documents', 'all'] });
    },
  });
}

export function useDeleteDocument(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (documentId: number): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/clients/${clientId}/documents/${documentId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['documents', 'client', clientId] });
      qc.invalidateQueries({ queryKey: ['documents', 'expiring'] });
    },
  });
}

export function useAllDocuments(filters: { q?: string; category?: string; page?: number } = {}) {
  return useQuery({
    queryKey: ['documents', 'all', filters],
    queryFn: async (): Promise<Paginated<DocumentData>> => {
      const { data } = await api.get<Paginated<DocumentData>>('/v1/documents', { params: filters });
      return data;
    },
  });
}

export function useExpiringDocuments(within = 90) {
  return useQuery({
    queryKey: EXPIRING_KEY(within),
    queryFn: async (): Promise<DocumentData[]> => {
      const { data } = await api.get<DocumentData[]>('/v1/documents/expiring', {
        params: { within },
      });
      return data;
    },
    staleTime: 5 * 60_000,
  });
}
